import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";

const HOURS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2)
    .toString()
    .padStart(2, "0");
  const minutes = i % 2 === 0 ? "00" : "30";
  return `${hour}:${minutes}`;
});

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TimeCalendarHorizontal = ({ scheduleDays, onToggle }) => {
  const [startHour, setStartHour] = useState("08:00");
  const [endHour, setEndHour] = useState("21:00");
  const visibleHours = HOURS.filter(
    (hour) => hour >= startHour && hour <= endHour,
  );

  const { t } = useTranslation();
  const [mode, setMode] = useState("CHECK_IN");
  const getDayData = (dayOfWeek) => {
    return (
      scheduleDays[0].checkScheduleDays.find(
        (d) => d.dayOfWeek?.toLowerCase() === dayOfWeek?.toLowerCase(),
      ) || {
        checkInHours: [],
        checkOutHours: [],
      }
    );
  };

  return (
    <TableContainer component={Paper}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        padding={2}
      >
        <Grid item>
          <Grid item>
            <FormControl size="small">
              <InputLabel id="start-hour-label">{t("from")}</InputLabel>
              <Select
                variant={"outlined"}
                labelId="start-hour-label"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                label={t("from")}
                style={{ minWidth: 100 }}
              >
                {HOURS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel id="end-hour-label">{t("to")}</InputLabel>
              <Select
                variant={"outlined"}
                labelId="end-hour-label"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                label={t("to")}
                style={{ minWidth: 100 }}
              >
                {HOURS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid item>
          <Button
            sx={{ mr: 1 }}
            variant="contained"
            style={{ backgroundColor: "#82b1ff" }}
            onClick={() => setMode("CHECK_IN")}
            data-testid="draw-checkin-mode-button"
          >
            {t("drawCheckInHours")}
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#ef9a9a" }}
            onClick={() => setMode("CHECK_OUT")}
            data-testid="draw-checkout-mode-button"
          >
            {t("drawCheckOutHours")}
          </Button>
        </Grid>
      </Grid>
      <Table
        size="small"
        style={{ borderCollapse: "collapse", tableLayout: "fixed" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 80 }}>Time</TableCell>
            {DAYS.map((day) => (
              <TableCell key={day} align="center">
                {t(day.toLowerCase())}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleHours.map((time) => (
            <TableRow key={time}>
              <TableCell>{time}</TableCell>
              {DAYS.map((day) => {
                const dayData = getDayData(day);
                const checkIn = new Set(
                  dayData.checkInHours?.map(String) || [],
                );
                const checkOut = new Set(
                  dayData.checkOutHours?.map(String) || [],
                );
                const isCheckIn = checkIn.has(`${time + ":00"}`);
                const isCheckOut = checkOut.has(`${time + ":00"}`);

                let bgColor = "#fff";
                if (isCheckIn && isCheckOut)
                  bgColor = "brown"; // both
                else if (isCheckIn) bgColor = "#82b1ff";
                else if (isCheckOut) bgColor = "#ef9a9a";

                return (
                  <TableCell
                    key={day}
                    onClick={() =>
                      onToggle({
                        time: time,
                        dayTimeMode: mode,
                        dayOfWeek: day.toUpperCase(),
                        id: scheduleDays[0].id,
                      })
                    }
                    style={{
                      cursor: "pointer",
                      backgroundColor: bgColor,
                      border: "1px solid #e0e0e0",
                      transition: "box-shadow 0.2s ease-in-out",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        mode === "CHECK_IN"
                          ? "inset 0 0 0 2px #82b1ff"
                          : "inset 0 0 0 2px #ef9a9a";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "none";
                    }}
                    data-testid={`time-slot-${day}-${time}`}
                  ></TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TimeCalendarHorizontal;
